import { ReactElement, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { HiChevronRight } from 'react-icons/hi2';
import FormCard from '../../components/FormCard';
import Button from '../../components/Button';
import {
  FEEDBACK_STATUS,
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_TYPE_LABELS,
  PRODUCT_AREA_LABELS,
} from '../../constants/feedback';
import { useFeedbackReport, useFeedbackReports } from '../../hooks/useFeedback';
import FeedbackReportBody from '../../components/Feedback/FeedbackReportBody';
import { useFeedbackTriageEditor } from '../../components/Feedback/useFeedbackTriageEditor';
import { inputClass } from '../../components/formControlClasses';
import type { FeedbackReport } from '../../api/feedback';

const fieldLabelClass = 'cr-text-xs cr-font-medium cr-text-gray-500';

interface TicketProps {
  report: FeedbackReport;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * One ticket: a summary row that expands in place.
 *
 * The list endpoint deliberately withholds the body, context and
 * attachments, so the full record is fetched on expand rather than
 * up front — a hundred reports would otherwise mean a hundred sets of
 * presigned screenshot URLs nobody looked at.
 */
function FeedbackTicket({ report, expanded, onToggle }: TicketProps): ReactElement {
  const { data: full, isPending } = useFeedbackReport(expanded ? report.id : null);
  const { ticketRef, setTicketRef, note, setNote, save, isSaving } = useFeedbackTriageEditor({
    reportId: report.id,
    report: full,
    closedToast: 'Ticket closed',
    initialTicketRef: report.ticket_ref ?? '',
  });
  const isClosed = report.status === FEEDBACK_STATUS.PROCESSED;

  return (
    <div className="cr-rounded-md cr-border cr-border-gray-200">
      <button
        className="cr-flex cr-w-full cr-cursor-pointer cr-items-center cr-gap-3 cr-border-0 cr-bg-transparent
          cr-p-3 cr-text-left hover:cr-bg-gray-50"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <HiChevronRight
          className={`cr-shrink-0 cr-transition-transform cr-duration-200 ${expanded ? 'cr-rotate-90' : ''}`}
          size={18}
        />
        <div className="cr-min-w-0 cr-flex-1">
          <div className="cr-truncate cr-font-medium cr-text-blue-900">{report.title}</div>
          <div className="cr-text-xs cr-text-gray-500">
            {report.user_name ?? 'Unknown user'}
            {report.team_name ? ` (${report.team_name})` : ''} · {FEEDBACK_TYPE_LABELS[report.feedback_type]} ·{' '}
            {PRODUCT_AREA_LABELS[report.product_area]} · {new Date(report.created_at).toLocaleString()}
            {report.ticket_ref ? ` · ${report.ticket_ref}` : ''}
          </div>
        </div>
        <span
          className={`cr-shrink-0 cr-rounded-full cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium ${
            isClosed ? 'cr-bg-gray-200 cr-text-gray-600' : 'cr-bg-blue-100 cr-text-blue-800'
          }`}
        >
          {isClosed ? 'Closed' : FEEDBACK_STATUS_LABELS[report.status]}
        </span>
      </button>

      {expanded && (
        <div className="cr-flex cr-flex-col cr-gap-3 cr-border-0 cr-border-t cr-border-solid cr-border-gray-200 cr-p-3">
          {isPending && <p className="cr-m-0 cr-text-sm cr-text-gray-500">Loading…</p>}

          {full && <FeedbackReportBody report={full} />}

          <label className="cr-flex cr-flex-col md:cr-w-[360px]">
            <span className={fieldLabelClass}>Stream Punk ticket</span>
            <input
              className={`cr-mt-1 ${inputClass}`}
              value={ticketRef}
              placeholder="Ticket UUID or reference"
              onChange={(event) => setTicketRef(event.target.value)}
            />
          </label>
          <label className="cr-flex cr-flex-col">
            <span className={fieldLabelClass}>Resolution note</span>
            <textarea
              className={`cr-mt-1 ${inputClass}`}
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          <div className="cr-flex cr-flex-wrap cr-gap-2">
            <Button variant="secondary" size="compact" normalCase disabled={isSaving} onClick={() => save()}>
              Save
            </Button>
            <Button
              variant="outline"
              size="compact"
              normalCase
              disabled={isSaving || isClosed}
              onClick={() => save(FEEDBACK_STATUS.PROCESSED)}
            >
              {isClosed ? 'Closed' : 'Close ticket'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * All feedback, as expanding tickets, on the admin page.
 *
 * The standalone triage page at /admin/feedback still exists for the
 * filtered table view; this is the at-a-glance version that sits with
 * the rest of the admin tools.
 */
export default function FeedbackSection(): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);

  // Empty status means "all"; the server treats an absent filter that way.
  const params = useMemo(
    () => (showClosed ? {} : { status: String(FEEDBACK_STATUS.NEW) }),
    [showClosed],
  );

  const { data: reports, isPending, error } = useFeedbackReports(params, isExpanded);

  useEffect(() => {
    if (error) toast.error('Could not load feedback reports');
  }, [error]);

  const openCount = reports?.filter((report) => report.status === FEEDBACK_STATUS.NEW).length ?? 0;

  return (
    <FormCard>
      <button
        className="cr-flex cr-w-full cr-items-center cr-justify-between cr-p-3 cr-text-left hover:cr-bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="cr-text-lg cr-font-semibold">
          Feedback
          {isExpanded && !showClosed && openCount > 0 && (
            <span className="cr-ml-2 cr-text-sm cr-font-normal cr-text-gray-500">{openCount} open</span>
          )}
        </h3>
        <HiChevronRight
          className={`cr-transition-transform cr-duration-200 ${isExpanded ? 'cr-rotate-90' : ''}`}
          size={20}
        />
      </button>

      {isExpanded && (
        <div className="cr-border-t cr-border-gray-200 cr-p-3">
          <label className="cr-mb-3 cr-flex cr-items-center cr-gap-2 cr-text-sm cr-text-gray-600">
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
            Show closed tickets
          </label>

          {isPending ? (
            <p className="cr-text-sm cr-text-gray-500">Loading feedback...</p>
          ) : !reports?.length ? (
            <p className="cr-text-sm cr-text-gray-500">
              {showClosed ? 'No feedback has been filed yet.' : 'No open tickets.'}
            </p>
          ) : (
            <div className="cr-flex cr-flex-col cr-gap-2">
              {reports.map((report) => (
                <FeedbackTicket
                  key={report.id}
                  report={report}
                  expanded={openTicketId === report.id}
                  onToggle={() => setOpenTicketId(openTicketId === report.id ? null : report.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </FormCard>
  );
}
